/**
 * The actual API calls.
 *
 * This runs inside the Approval app, so a call here is the same call the app makes: same origin,
 * same `credentials: 'include'` cookie jar, same CORS preflight, same headers `RequestBuilder`
 * puts on every request (`utils/api/api.impl.js`). Nothing proxies it and nothing replays a stored
 * cookie - it shows up in the Network tab next to the app's own traffic, and you can copy it as
 * cURL from there.
 *
 * The calls are built here rather than through `utils/api/api.js` on purpose: the point of the
 * tool is to see the raw request, so the header set is spelled out and each header is
 * individually switchable.
 */
import {isConnectLogin} from "utils/toggleConnectOdp.js";
import {API_BASE_PATHS} from "./endpoints.js";

/**
 * The app's own API root, e.g. https://oauth.approval.aws.stag.visma.net/approval - already
 * ending in /approval, with /rest and /jaxrs mounted under it. Same choice
 * `utils/api/api.js#getApiRootUrl` makes, minus its /jaxrs suffix.
 */
export function apiRoot() {
    const environment = window.ENVIRONMENT ?? {};
    const root = isConnectLogin() ? environment.APPROVAL_API_OAUTH : environment.APPROVAL_API;
    if (!root) {
        throw new Error("No API root: window.ENVIRONMENT is not set on this page.");
    }
    /*
     * Trailing slashes are counted off rather than stripped with /\/+$/. That pattern backtracks
     * quadratically on a value ending in many slashes followed by anything else, because the engine
     * retries the run of slashes from every position. This is a deployment-set config value, so it
     * was never reachable input - but a loop is linear and leaves nothing to argue about.
     */
    const value = String(root);
    let end = value.length;
    while (end > 0 && value.charAt(end - 1) === "/") {
        end--;
    }
    return value.slice(0, end);
}

/**
 * Substitutes `{name}` placeholders from params. Most operations on both APIs are addressed by a
 * uid or key in the path (20 of 30 on the Task API, 197 of 255 on the Web API), so this is the
 * common case rather than a special one.
 */
function fillPath(path, params) {
    return path.replace(/\{(\w+)\}/g, (_match, name) => {
        const value = params[name];
        if (value == null || String(value).trim() === "") {
            throw new Error(`Missing path parameter "${name}" for ${path}`);
        }
        return encodeURIComponent(String(value).trim());
    });
}

function buildUrl(side, spec, params) {
    const url = new URL(apiRoot() + API_BASE_PATHS[side] + fillPath(spec.path, params));
    for (const [name, value] of Object.entries(spec.query?.(params) ?? {})) {
        if (value != null && String(value).trim() !== "") {
            url.searchParams.set(name, String(value).trim());
        }
    }
    return url.toString();
}

/**
 * `RequestBuilder` sends either JSON or url-encoded form data, and which one an endpoint wants is
 * not guessable from the method - the Web API uses both. The spec declares it.
 */
function buildBody(spec, params, headers) {
    if (spec.body == null) {
        return undefined;
    }
    const data = spec.body(params) ?? {};
    if (spec.bodyType === "form") {
        headers["content-type"] = "application/x-www-form-urlencoded;charset=UTF-8";
        const form = new URLSearchParams();
        for (const [name, value] of Object.entries(data)) {
            if (value != null) {
                form.set(name, String(value));
            }
        }
        return form.toString();
    }
    headers["content-type"] = "application/json;charset=UTF-8";
    return JSON.stringify(data);
}

/**
 * Same header set `RequestBuilder` puts on every call. `sessionHash` and `currentContext` are
 * globals the app publishes once /startup has answered (`components/usercontext/user.action.js`).
 */
function buildHeaders(spec, options) {
    const headers = {
        "accept": spec.accept ?? "application/json",
        "x-requested-with": "XMLHttpRequest",
    };
    if (options.ignoreCookieAuth) {
        headers["x-ignore-cookie-auth"] = "true";
    }
    if (options.sendSessionHash && window.sessionHash != null) {
        headers["x-session-hash"] = String(window.sessionHash);
    }
    const contextId = options.contextId || window.currentContext;
    if (options.sendContextId && contextId != null) {
        headers["visma-contextid"] = String(contextId);
    }
    return headers;
}

/** Only the non-HttpOnly ones are visible to script; the session cookie deliberately is not. */
function readableCookieNames() {
    return document.cookie.split(";")
        .map(part => part.trim().split("=")[0])
        .filter(Boolean);
}

/**
 * One side of the comparison, called exactly the way the app would call it.
 * @returns {Promise<object>} the full request/response record
 */
export async function callSide(side, spec, params, options) {
    const headers = buildHeaders(spec, options);

    // a missing path parameter is a caller error, not a failed request
    let url;
    let body;
    try {
        url = buildUrl(side, spec, params);
        body = buildBody(spec, params, headers);
    } catch (error) {
        return {api: side, request: {method: spec.method, url: spec.path, headers}, error: error.message};
    }

    const record = {
        api: side,
        request: {
            method: spec.method,
            url,
            headers,
            body,
            cookieNames: readableCookieNames(),
        },
    };

    const startedAt = performance.now();
    let response;
    try {
        response = await fetch(url, {
            method: spec.method,
            headers,
            body,
            // the app's own SHARED_PARAMETERS
            credentials: "include",
            mode: "cors",
            cache: "default",
            // some endpoints answer 303 to a tenant-scoped URL, same as the app's own fetch
            redirect: "follow",
        });
    } catch (error) {
        record.durationMs = Math.round(performance.now() - startedAt);
        // a CORS rejection and a network failure both land here, and the browser deliberately
        // hides which - the DevTools Network entry for this call has the real reason
        record.error = `${error.name}: ${error.message}`;
        return record;
    }

    const text = await response.text();
    record.durationMs = Math.round(performance.now() - startedAt);
    record.response = {
        status: response.status,
        statusText: response.statusText,
        finalUrl: response.redirected ? response.url : null,
        contentType: response.headers.get("content-type"),
        bytes: new TextEncoder().encode(text).length,
        headers: Object.fromEntries([...response.headers.entries()]),
    };

    try {
        record.body = text === "" ? null : JSON.parse(text);
    } catch {
        record.body = null;
        record.rawBody = text.slice(0, 4000);
    }

    record.items = record.body == null ? [] : spec.extract(record.body);
    return record;
}

/**
 * GET /jaxrs/startup - the session probe, and the source of the two values the app puts on every
 * later request. A real call like any other, so it shows up in the Network tab too.
 */
export async function fetchStartup() {
    const url = apiRoot() + API_BASE_PATHS.web + "/startup";
    const response = await fetch(url, {
        method: "GET",
        headers: buildHeaders({accept: "application/json"}, {ignoreCookieAuth: isConnectLogin()}),
        credentials: "include",
        mode: "cors",
        cache: "default",
        // /startup answers 303 to the tenant-scoped /tenant/{companyId}/startup
        redirect: "follow",
    });
    if (response.status !== 200) {
        return {ok: false, status: response.status, statusText: response.statusText, url};
    }
    return {ok: true, status: 200, url, body: await response.json()};
}

/**
 * GET /jaxrs/me/tenants - the companies this user can switch between, as `[{id, name}]`. The same
 * list the header's context selector shows (`utils/api/api.js#getUserContexts`), and `id` is the
 * context id that the `companyId` query parameter expects.
 */
export async function fetchCompanies() {
    const url = apiRoot() + API_BASE_PATHS.web + "/me/tenants";
    const response = await fetch(url, {
        method: "GET",
        headers: buildHeaders({accept: "application/json"}, {ignoreCookieAuth: isConnectLogin()}),
        credentials: "include",
        mode: "cors",
        cache: "default",
        redirect: "follow",
    });
    if (response.status !== 200) {
        return {ok: false, status: response.status, statusText: response.statusText, url};
    }
    const body = await response.json();
    const companies = Array.isArray(body) ? body : [];
    return {
        ok: true,
        companies: [...companies].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    };
}

/**
 * The tasks to offer for the `taskUids` filter, narrowed to one company when one is picked.
 *
 * Read from the Web API's own my-tasks - that is the API the parameter belongs to, so its list is
 * the one whose uids are valid there. This is a separate call from the comparison run and shows up
 * in the Network tab as such.
 */
export async function fetchTasks(companyId) {
    const url = new URL(apiRoot() + API_BASE_PATHS.web + "/my-tasks");
    if (companyId != null && String(companyId).trim() !== "") {
        url.searchParams.set("companyId", String(companyId).trim());
    }
    const response = await fetch(url.toString(), {
        method: "GET",
        headers: buildHeaders({accept: "application/json"}, {ignoreCookieAuth: isConnectLogin()}),
        credentials: "include",
        mode: "cors",
        cache: "default",
        redirect: "follow",
    });
    if (response.status !== 200) {
        return {ok: false, status: response.status, statusText: response.statusText, url: url.toString()};
    }
    const body = await response.json();
    return {ok: true, tasks: Array.isArray(body?.tasks) ? body.tasks : []};
}

export function describeSession(startup) {
    const user = startup?.loggedInUser ?? {};
    const conf = startup?.userConfiguration ?? {};
    return {
        name: [user.firstName, user.lastName].filter(Boolean).join(" "),
        email: user.emailAddress,
        userId: user.id,
        connectUserId: user.connectUserId,
        // currentContext is the companyId the tenant-scoped URLs use, e.g. /tenant/4599244/startup
        contextId: conf.currentContext,
        companyId: conf.currentCompany?.odpcompanyId?.odpCompanyId ?? conf.currentContext,
        tenantId: conf.currentCompany?.tenantId,
        companyName: conf.companyName,
        sessionHash: startup?.sessionHash,
    };
}
